import { UploadOutlined } from '@ant-design/icons';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { App, Image, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { delImage, uploadAuthentication, uploadFlie, uploadSetImg } from '@/services/common';
import { compressImage } from '@/utils/common';

// 提取 beforeUpload 的文件类型
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const UpLoadImage: React.FC<{
	type: 'order' | 'store' | 'authentication';
	requestParams?: { [key: string]: any };
	dataList: string[];
	limit: number;
	multiple?: boolean;
	showPreviewIcon?: boolean;
	showRemoveIcon?: boolean;
	imgChange?: (fileList: string[]) => void;
}> = ({
	type = 'order',
	requestParams = null,
	dataList = [],
	limit = 3,
	multiple = false,
	showPreviewIcon = true,
	showRemoveIcon = true,
	imgChange,
}) => {
	const { message } = App.useApp();
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [uploading, setUploading] = useState(false); // 上传中状态

	useEffect(() => {
		if (dataList.length > 0) {
			const imageList: UploadFile[] = dataList?.map((img) => ({
				uid: uuidv4(),
				name: img,
				status: 'done',
				url: img.startsWith('http') ? img : `${API_URL}${img}`,
			}));
			setFileList(imageList);
		}
	}, [dataList]);

	// 阻止默认上传（必须，因为用了 customRequest）
	const beforeUpload = (file: FileType) => {
		const isVideo = file.type.startsWith('video/');
		if (isVideo) return;
		// 限制文件类型（可选）
		const isImage = file.type.startsWith('image/');
		if (!isImage) {
			message.error('只能上传图片格式！');
			return false;
		}
		// 限制文件大小（可选，单位：MB）
		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			message.error('图片大小不能超过 2MB！');
			return false;
		}
		return; // 返回 false 阻止默认上传
	};

	// 自定义上传逻辑（核心：压缩 + 接口请求）
	const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError, onProgress }) => {
		try {
			setUploading(true);
			const isVideo = (file as File).type?.startsWith('video/');
			// 1. 压缩图片
			const compressedFile = isVideo ? file : await compressImage(file as File, 0.5, 1920);
			// console.log('customRequest-compressedFile', compressedFile);
			// 2. 构造 FormData（接口要求单图上传）
			const formData = new FormData();
			// 接口接收的字段名，根据实际调整
			formData.append('file', compressedFile);
			// 如果额外请求参数存在则循环添加
			requestParams &&
				Object.entries(requestParams).forEach(([key, value]) => {
					formData.append(key, value);
				});

			// 3. 上传接口请求
			let res = null;
			if (type === 'order') {
				res = await uploadSetImg(formData, {
					// 展示进度条
					onUploadProgress: (e: any) => {
						// console.log(e);
						const percent = Math.round((e.loaded * 100) / (e.total || 1));
						onProgress?.({ percent }, file);
					},
				});
			}
			if (type === 'store') {
				res = await uploadFlie(formData, {
					// 展示进度条
					onUploadProgress: (e: any) => {
						// console.log(e);
						const percent = Math.round((e.loaded * 100) / (e.total || 1));
						onProgress?.({ percent }, file);
					},
				});
			}
			if (type === 'authentication') {
				res = await uploadAuthentication(formData, {
					// 展示进度条
					onUploadProgress: (e: any) => {
						// console.log(e);
						const percent = Math.round((e.loaded * 100) / (e.total || 1));
						onProgress?.({ percent }, file);
					},
				});
			}

			// 4. 接口返回处理（假设返回格式：{ code: 200, data: ['https://xxx.jpg'] }）
			const { code, data } = res || {};
			if (code === 1) {
				let url = '';
				if (type === 'order') {
					url = data[data.length - 1];
				}
				if (type === 'store') {
					url = data.file;
				}
				if (type === 'authentication') {
					url = data.url;
				}
				// 上传成功：更新 file 状态，适配 Antd Upload 格式
				onSuccess?.(
					// 接口返回的最后一张图片地址
					{ url },
					file,
				);
				message.success('文件上传成功');
			} else {
				throw new Error('接口返回格式异常');
			}
		} catch (error) {
			onError?.(error as Error, file);
			message.error('文件上传失败，请重试');
			console.error('上传失败：', error);
		} finally {
			setUploading(false);
		}
	};

	// 预览
	const handlePreview = async (file: UploadFile) => {
		setPreviewImage(file.url || file.response.url);
		setPreviewOpen(true);
	};

	// 删除
	const handleRemove: UploadProps['onRemove'] = async (file: UploadFile) => {
		if (type !== 'order') {
			return true;
		} else {
			setUploading(true);
			const res = await delImage({
				imgUrl: file.url || file.response.url,
				order_id: requestParams?.name,
			});
			const { code, data } = res || {};
			const { msg } = data || {};
			if (code === 1) {
				const filteredList = fileList.filter((preFile) => preFile.uid !== file.uid);
				setFileList(filteredList);
				returnImages(filteredList);
				message.success(msg);
				setUploading(false);
				return true;
			} else {
				message.error(msg);
				setUploading(false);
				return false;
			}
		}
	};

	// 文件列表变化
	const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
		// 过滤掉上传失败的文件
		const filteredList = newFileList.filter((file) => file && file.status !== 'error');

		setFileList(filteredList);
		returnImages(filteredList);
	};

	const returnImages = (fileList: UploadFile[]) => {
		const reImageList = fileList.reduce((acc, file) => {
			const url = file?.url || file?.response?.url;
			// 仅当url存在时，添加到结果数组
			if (url) {
				acc.push(url);
			}
			return acc;
		}, [] as string[]);
		reImageList.length && imgChange?.(reImageList);
	};

	return (
		<>
			<Upload
				listType="picture-card"
				maxCount={limit}
				multiple={multiple}
				fileList={fileList}
				onPreview={handlePreview}
				onChange={handleChange}
				onRemove={handleRemove}
				customRequest={customRequest} // 自定义上传
				beforeUpload={beforeUpload} // 阻止默认上传 + 校验
				showUploadList={{
					showPreviewIcon: showPreviewIcon,
					showRemoveIcon: showRemoveIcon,
				}}
				disabled={uploading} // 上传中/达到上限时禁用
			>
				{!uploading && fileList.length < limit && (
					<div>
						<UploadOutlined />
						<div style={{ marginTop: 8 }}>上传文件</div>
					</div>
				)}
			</Upload>

			{/* 预览图片（保留原有） */}
			{previewImage && (
				<Image
					wrapperStyle={{ display: 'none' }}
					preview={{
						visible: previewOpen,
						onVisibleChange: (visible) => setPreviewOpen(visible),
						afterOpenChange: (visible) => !visible && setPreviewImage(''),
					}}
					src={previewImage}
				/>
			)}
		</>
	);
};

export default UpLoadImage;
